import { Box, Package as PackageIcon, Scale } from "lucide-react";

import { useProductPackages } from "@/api/queries";
import type { PackageType, Product, ProductType, TrackingMode } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDimensions, formatWeight } from "@/lib/units";
import { formatQuantity, formatUnit } from "@/lib/format";

/**
 * Le produit comme objet physique (TICKET PM-01).
 *
 * Une donnée qui n'existe qu'en base n'est pas livrée. Cette carte affiche ce
 * que le Product Master sait désormais du produit — ce qu'il est, comment son
 * stock est suivi, ce qu'il pèse, ce qu'il mesure, et sous quelles formes il
 * s'achète.
 *
 * Elle ne montre que ce qui est renseigné. Un produit créé en dix secondes
 * depuis une étagère n'a rien de tout ça, et une grille de tirets ne rend
 * service à personne.
 */

const PRODUCT_TYPES: Record<ProductType, string> = {
  CONSUMABLE: "Consommable",
  ASSET_TRACKED: "Outil suivi",
  MEASURED_MATERIAL: "Matériau mesuré",
  COMPONENT: "Composant",
  SPARE_PART: "Pièce de rechange",
  KIT: "Ensemble",
  ASSEMBLY: "Assemblage",
  OTHER: "Autre",
};

const TRACKING_MODES: Record<TrackingMode, string> = {
  QUANTITY: "à la quantité",
  ASSET: "à l'exemplaire",
  MEASURED: "à la mesure",
  LOT: "par lot",
};

const PACKAGE_TYPES: Record<PackageType, string> = {
  EACH: "Unité",
  BAG: "Sac",
  BOX: "Boîte",
  CASE: "Caisse",
  PACK: "Paquet",
  ROLL: "Rouleau",
  SPOOL: "Bobine",
  REEL: "Touret",
  BUNDLE: "Botte",
  PALLET: "Palette",
  DRUM: "Baril",
  BUCKET: "Chaudière",
  TUBE: "Tube",
  OTHER: "Autre",
};

export function ProductPhysicalCard({ product }: { product: Product }) {
  const { data: packages } = useProductPackages(product.id);
  const dimensions = formatDimensions(product.length_mm, product.width_mm, product.height_mm);
  const hasPhysical = product.net_weight_mg !== null || dimensions !== null;

  return (
    <Card>
      <CardHeader
        title="Physique et conditionnements"
        subtitle="Ce que le produit pèse, mesure, et sous quelles formes il s'achète."
      />
      <CardBody className="flex flex-col gap-3">
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
          <Row label="Type" value={PRODUCT_TYPES[product.product_type]} />
          <Row label="Suivi" value={TRACKING_MODES[product.tracking_mode]} />
          <Row label="Unité de stock" value={formatUnit(product.stock_unit)} />
          <Row
            label="Achat / sortie"
            value={
              product.purchase_unit || product.issue_unit
                ? [product.purchase_unit, product.issue_unit]
                    .map((unit) => (unit ? formatUnit(unit) : "—"))
                    .join(" / ")
                : null
            }
          />
        </dl>

        {hasPhysical && (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border pt-3 text-[13px]">
            {product.net_weight_mg !== null && (
              <Row
                label="Poids net"
                value={formatWeight(product.net_weight_mg)}
                icon={<Scale className="size-3.5" aria-hidden />}
              />
            )}
            {dimensions && (
              <Row
                label="Dimensions"
                value={dimensions}
                icon={<Box className="size-3.5" aria-hidden />}
              />
            )}
          </dl>
        )}

        <div className="border-t border-border pt-3">
          <p className="mb-2 flex items-center gap-1.5 text-[12px] text-ink-muted">
            <PackageIcon className="size-3.5" aria-hidden />
            Conditionnements
          </p>
          {packages && packages.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {packages.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] text-ink">{entry.name}</span>
                    <span className="block text-[11px] text-ink-subtle">
                      {PACKAGE_TYPES[entry.package_type]} ·{" "}
                      {formatQuantity(entry.quantity)} {formatUnit(entry.unit)}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {entry.is_default_purchase && (
                      <StatusBadge tone="info">achat</StatusBadge>
                    )}
                    {entry.is_default_issue && <StatusBadge tone="unknown">sortie</StatusBadge>}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-ink-subtle">
              Aucun. Le produit se reçoit et se sort dans son unité de stock.
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function Row({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | null;
  icon?: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-[11px] text-ink-subtle">
        {icon}
        {label}
      </dt>
      <dd className="truncate text-ink">{value ?? "—"}</dd>
    </div>
  );
}
